import { FormStepRenderer } from "@/components/register/FormStepRenderer";
import { NavigationButtons } from "@/components/register/NavigationButtons";
import StepIndicator from "@/components/register/StepIndicator";
import { authService, SecondStepData } from "@/services/auth.service/auth.api";
import confetti from "canvas-confetti";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const REGISTER_USER_ID_KEY = "register_userId";
const REGISTER_STEP_KEY = "register_step";
const REGISTER_SUBDOMAIN_KEY = "register_subdomain";

export default function Register() {
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem(REGISTER_STEP_KEY);
    const step = saved ? parseInt(saved, 10) : 1;
    return step >= 1 && step <= 2 ? step : 1;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState(() => localStorage.getItem(REGISTER_USER_ID_KEY) || "");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Step 1
    email: null,
    name: null,
    password: null,
    confirmPassword: null,
    phone: null,
    // Step 2
    documentType: "cpf" as "cpf" | "cnpj",
    document: null,
    subdomain: null,
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateCurrentStep = () => {
    if (currentStep === 1) {
      if (!formData.email || !formData.name || !formData.password || !formData.confirmPassword || !formData.phone) {
        toast.error("Preencha todos os campos");
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error("As senhas não coincidem");
        return false;
      }
    } else if (currentStep === 2) {
      if (!formData.document || !formData.subdomain) {
        toast.error("Preencha todos os campos");
        return false;
      }
    }

    return true;
  };

  const handleFirstStep = async () => {
    setIsLoading(true);
    try {
      const [response, error] = await authService.firstStep({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
      });

      if (error) {
        if (error.code === 'user-already-have-client') {
          const data = error.data || {};
          const id = data.userId != null ? String(data.userId) : "";
          const step = typeof data.step === "number" ? data.step : parseInt(data.step, 10) || 2;
          setUserId(id);
          setCurrentStep(step);
          if (id) localStorage.setItem(REGISTER_USER_ID_KEY, id);
          if (step >= 1 && step <= 2) localStorage.setItem(REGISTER_STEP_KEY, String(step));
          if (step === 3) {
            localStorage.removeItem(REGISTER_USER_ID_KEY);
            localStorage.removeItem(REGISTER_STEP_KEY);
            toast.info("Faça login e complete seu pagamento em Assinatura.");
            navigate("/login");
            return false;
          }
          if (data.subdomain) {
            setFormData((prev) => ({ ...prev, subdomain: data.subdomain }));
            localStorage.setItem(REGISTER_SUBDOMAIN_KEY, data.subdomain);
          }
          if (step === 4) {
            localStorage.removeItem(REGISTER_USER_ID_KEY);
            localStorage.removeItem(REGISTER_STEP_KEY);
            toast.info("Cadastro já finalizado. Faça login para acessar.");
            navigate('/login');
            return false;
          }
          toast.info("Continue seu cadastro");
          return true;
        }
        console.error("Erro ao criar conta:", error);
        return false;
      }

      if (response && response.userId) {
        setUserId(response.userId);
        localStorage.setItem(REGISTER_USER_ID_KEY, response.userId);
        setCurrentStep(2);
        localStorage.setItem(REGISTER_STEP_KEY, "2");
        return true;
      }

      toast.error("Erro ao criar conta", {
        style: {
          backgroundColor: 'hsl(var(--destructive))',
          color: 'hsl(var(--destructive-foreground))',
          borderColor: 'hsl(var(--destructive))'
        }
      });
      return false;
    } catch (err) {
      console.error("Erro no primeiro passo:", err);
      toast.error("Erro ao criar conta", {
        style: {
          backgroundColor: 'hsl(var(--destructive))',
          color: 'hsl(var(--destructive-foreground))',
          borderColor: 'hsl(var(--destructive))'
        }
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const checkUserStatus = async () => {
    try {
      const [response, error] = await authService.firstStep({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
      });

      if (error && error.code === 'user-already-have-client') {
        const data = error.data || {};
        const id = data.userId != null ? String(data.userId) : "";
        const step = typeof data.step === "number" ? data.step : parseInt(data.step, 10) || 2;
        setUserId(id);
        setCurrentStep(step);
        if (id) localStorage.setItem(REGISTER_USER_ID_KEY, id);
        if (step >= 1 && step <= 2) localStorage.setItem(REGISTER_STEP_KEY, String(step));
        if (step === 3) {
          localStorage.removeItem(REGISTER_USER_ID_KEY);
          localStorage.removeItem(REGISTER_STEP_KEY);
          navigate("/login");
          return true;
        }
        if (data.subdomain) {
          setFormData((prev) => ({ ...prev, subdomain: data.subdomain }));
          localStorage.setItem(REGISTER_SUBDOMAIN_KEY, data.subdomain);
        }
        if (step === 4) {
          localStorage.removeItem(REGISTER_USER_ID_KEY);
          localStorage.removeItem(REGISTER_STEP_KEY);
          navigate("/login");
        }
        return true;
      }
    } catch (err) {
      console.error("Erro ao verificar status do usuário:", err);
    }
    return false;
  };

  const submitSecondStep = useCallback(async () => {
    const secondStepData: SecondStepData = {
      cpfCnpj: formData.document,
      subdomain: formData.subdomain
    };
    setIsLoading(true);
    try {
      const [response, error] = await authService.secondStep(userId, secondStepData);

      if (error) {
        toast.error(error.msg || "Erro ao salvar informações da empresa", {
          style: {
            backgroundColor: 'hsl(var(--destructive))',
            color: 'hsl(var(--destructive-foreground))',
            borderColor: 'hsl(var(--destructive))'
          }
        });
        return false;
      }

      if (!response) {
        toast.error("Erro ao salvar informações da empresa", {
          style: {
            backgroundColor: 'hsl(var(--destructive))',
            color: 'hsl(var(--destructive-foreground))',
            borderColor: 'hsl(var(--destructive))'
          }
        });
        return false;
      }

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      localStorage.removeItem(REGISTER_USER_ID_KEY);
      localStorage.removeItem(REGISTER_STEP_KEY);
      localStorage.removeItem(REGISTER_SUBDOMAIN_KEY);

      try {
        await authService.login({
          email: formData.email,
          password: formData.password,
          subdomain: formData.subdomain
        });
        setTimeout(() => navigate("/dashboard"), 1500);
      } catch (loginError) {
        console.error("Auto login failed:", loginError);
        toast.error("Erro ao fazer login automático", {
          style: {
            backgroundColor: 'hsl(var(--destructive))',
            color: 'hsl(var(--destructive-foreground))',
            borderColor: 'hsl(var(--destructive))'
          }
        });
        setTimeout(() => navigate("/login"), 1500);
      }

      return true;
    } catch (err) {
      console.error("Erro no segundo passo:", err);
      toast.error("Erro ao salvar informações da empresa", {
        style: {
          backgroundColor: 'hsl(var(--destructive))',
          color: 'hsl(var(--destructive-foreground))',
          borderColor: 'hsl(var(--destructive))'
        }
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [userId, formData, navigate]);

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    try {
      if (currentStep === 1) {
        const success = await handleFirstStep();
        if (success) {
          setCurrentStep(2);
          localStorage.setItem(REGISTER_STEP_KEY, "2");
        }
      } else if (currentStep === 2) {
        await submitSecondStep();
      }
    } catch (error) {
      console.error("Erro ao processar o passo:", error);
      toast.error("Ocorreu um erro ao processar o formulário");
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      navigate('/dashboard');
    } else {
      const savedEmail = localStorage.getItem('register_email');
      const savedSubdomain = localStorage.getItem(REGISTER_SUBDOMAIN_KEY);
      if (savedEmail) setFormData((prev) => ({ ...prev, email: savedEmail }));
      if (savedSubdomain) setFormData((prev) => ({ ...prev, subdomain: savedSubdomain }));
      if (savedEmail) checkUserStatus();
    }
  }, []);

  const stepTitles: Record<number, { title: string; subtitle: string }> = {
    1: {
      title: "Crie sua conta",
      subtitle: "Informe seus dados pessoais para começar.",
    },
    2: {
      title: "Sua organização",
      subtitle: "CPF ou CNPJ e o endereço único do seu espaço na plataforma.",
    },
  };

  const header = stepTitles[currentStep] ?? stepTitles[1];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-background via-background to-muted/30 p-4 sm:p-6">
      <div className="w-full max-w-lg animate-fadeIn">
        <div className="rounded-2xl border border-border/80 bg-card/95 shadow-xl shadow-black/5 backdrop-blur-sm p-6 sm:p-8">
          <div className="text-center mb-8 space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{header.title}</h1>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-md mx-auto">
              {header.subtitle}
            </p>
          </div>

          <StepIndicator currentStep={currentStep} totalSteps={2} />

          <form className="space-y-1">
            <FormStepRenderer
              currentStep={currentStep}
              formData={formData}
              handleChange={handleChange}
            />

            <NavigationButtons
              currentStep={currentStep}
              onNext={handleNext}
              onBack={currentStep > 1 ? handleBack : undefined}
              isLoading={isLoading}
            />
          </form>
        </div>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Já tem uma conta?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline underline-offset-4">
            Faça login
          </Link>
        </p>
      </div>
    </div>
  );
}
